import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";

const GRID_SIZE = 20;
const INITIAL_SNAKE = [[10, 10]];
const INITIAL_FOOD = [5, 5];
const INITIAL_DIRECTION = [0, -1];
const INITIAL_SPEED = 200;

function Snake() {
  const [snake, setSnake] = useState(INITIAL_SNAKE);
  const [food, setFood] = useState(INITIAL_FOOD);
  const [direction, setDirection] = useState(INITIAL_DIRECTION);
  const [speed, setSpeed] = useState(INITIAL_SPEED);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [paused, setPaused] = useState(false);

  const directionRef = useRef(direction);
  directionRef.current = direction;
  const touchStartRef = useRef(null);

  const DIRECTIONS = {
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0],
  };

  const generateFood = (snake) => {
    let newFood;
    do {
      newFood = [
        Math.floor(Math.random() * GRID_SIZE),
        Math.floor(Math.random() * GRID_SIZE),
      ];
    } while (snake.some(([x, y]) => x === newFood[0] && y === newFood[1]));
    return newFood;
  };

  const handleKeyDown = (e) => {
    if (e.key === "p" || e.key === "P") {
      setPaused((prev) => !prev);
      return;
    }
    const newDir = DIRECTIONS[e.key];
    if (newDir) {
      const [dx, dy] = newDir;
      const [cx, cy] = directionRef.current;
      if (dx !== -cx || dy !== -cy) setDirection(newDir);
    }
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleTouchEnd = (e) => {
    if (!touchStartRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchStartRef.current.x;
    const dy = touch.clientY - touchStartRef.current.y;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0 && directionRef.current[0] !== -1) setDirection([1, 0]);
      else if (dx < 0 && directionRef.current[0] !== 1) setDirection([-1, 0]);
    } else {
      if (dy > 0 && directionRef.current[1] !== -1) setDirection([0, 1]);
      else if (dy < 0 && directionRef.current[1] !== 1) setDirection([0, -1]);
    }
    touchStartRef.current = null;
  };

  const moveSnake = () => {
    if (paused || gameOver) return;

    setSnake((prevSnake) => {
      const newHead = [
        prevSnake[0][0] + directionRef.current[0],
        prevSnake[0][1] + directionRef.current[1],
      ];

      if (
        newHead[0] < 0 ||
        newHead[1] < 0 ||
        newHead[0] >= GRID_SIZE ||
        newHead[1] >= GRID_SIZE
      ) {
        setGameOver(true);
        return prevSnake;
      }

      if (prevSnake.some(([x, y]) => x === newHead[0] && y === newHead[1])) {
        setGameOver(true);
        return prevSnake;
      }

      const newSnake = [newHead, ...prevSnake];

      if (newHead[0] === food[0] && newHead[1] === food[1]) {
        setScore((s) => s + 1);
        setFood(generateFood(newSnake));
        if (speed > 50) setSpeed((s) => s - 5);
      } else {
        newSnake.pop();
      }

      return newSnake;
    });
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    if (gameOver) return;
    const interval = setInterval(moveSnake, speed);
    return () => clearInterval(interval);
  }, [speed, gameOver, paused]);

  const restartGame = () => {
    setSnake(INITIAL_SNAKE);
    setFood(INITIAL_FOOD);
    setDirection(INITIAL_DIRECTION);
    setSpeed(INITIAL_SPEED);
    setScore(0);
    setGameOver(false);
    setPaused(false);
  };

  return (
    <div
      className="snake-page"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      style={{
        fontFamily: "Arial, sans-serif",
        background: "linear-gradient(to bottom, #111, #222)",
        color: "#fff",
        minHeight: "100vh",
        padding: "10px",
        textAlign: "center",
      }}
    >
      {/* SEO Meta */}
      <Helmet>
        <title>Classic Snake Game Online - Play Free Snake</title>
        <meta
          name="description"
          content="Play the classic Snake game online for free. Responsive for mobile and desktop. Addictive gameplay with scoring and pause/resume."
        />
        <meta
          name="keywords"
          content="snake game, classic snake, online snake, free snake game, responsive game, play snake online"
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      <header>
        <h1>Classic Snake Game</h1>
        <p>Play online on mobile or desktop. Press P to pause/resume.</p>
      </header>

      <h2>Score: {score} {paused && "(Paused)"}</h2>

      {gameOver && (
        <div style={{ margin: "15px 0" }}>
          <h2>Game Over 😢</h2>
          <button
            onClick={restartGame}
            style={{
              padding: "10px 25px",
              fontSize: "16px",
              cursor: "pointer",
              backgroundColor: "#00ff00",
              color: "#111",
              border: "none",
              borderRadius: "5px",
              marginTop: "10px",
            }}
          >
            Restart
          </button>
        </div>
      )}

      {/* Game Grid */}
      <div
        className="grid"
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(${GRID_SIZE}, min(25px, 4vw))`,
          gridTemplateRows: `repeat(${GRID_SIZE}, min(25px, 4vw))`,
          backgroundColor: "#222",
          border: "2px solid #555",
          margin: "0 auto 10px auto",
        }}
      >
        {Array.from({ length: GRID_SIZE }).map((_, y) =>
          Array.from({ length: GRID_SIZE }).map((_, x) => {
            const isSnake = snake.some(([sx, sy]) => sx === x && sy === y);
            const isFood = food[0] === x && food[1] === y;
            return (
              <div
                key={`${x}-${y}`}
                style={{
                  width: "100%",
                  height: "100%",
                  border: "1px solid #333",
                  boxSizing: "border-box",
                  background: isSnake
                    ? "linear-gradient(to right, #00ff00, #00aa00)"
                    : isFood
                    ? "red"
                    : "transparent",
                  borderRadius: isFood ? "50%" : "4px",
                  boxShadow: isSnake
                    ? "0 0 5px #00ff00"
                    : isFood
                    ? "0 0 8px #ff0000"
                    : "none",
                  transition: "background-color 0.1s",
                }}
              />
            );
          })
        )}
      </div>

      <p>Use arrow keys on PC or swipe on mobile.</p>

      {/* AdSense Placeholder */}
      <div
        style={{
          marginTop: "20px",
          textAlign: "center",
        }}
      >
        <div
          style={{
            width: "320px",
            height: "100px",
            backgroundColor: "#ccc",
            display: "inline-block",
            lineHeight: "100px",
            color: "#111",
          }}
        >
          Ad Space
        </div>
      </div>

      {/* Instructions */}
      <section style={{ maxWidth: "600px", margin: "20px auto", color: "#ccc" }}>
        <h2>How to Play</h2>
        <p>
          Move the snake to eat food and grow longer. Avoid walls and your own tail.
          Arrow keys or swipe control the snake. Each food eaten increases your score and speed.
        </p>
        <p>Fully responsive for mobile and desktop. Play responsibly and enjoy!</p>
      </section>

      <footer style={{ marginTop: "20px", fontSize: "14px", color: "#ccc" }}>
        <p>
          Disclaimer: This game does not collect personal data. Ads may be displayed for revenue.
        </p>
      </footer>
    </div>
  );
}

export default Snake;
